import * as ol from "ol";
import * as Extent from "ol/extent";
import VectorLayer from "ol/layer/Vector";
import * as Proj from "ol/proj";
import * as React from "react";
import { Area } from "../entities/Area";
import { snapAreaToGrid } from "../utils/Area";
import { useOpenLayersDrag } from "./useOpenLayersDrag";

type Coordinate = [number, number];
const [leftMost, , rightMost] = [...Proj.fromLonLat([-180, 90]), ...Proj.fromLonLat([180, -90])];
const worldWidth = rightMost - leftMost;
const halfWorldWidth = worldWidth / 2;

type DragState =
	| { type: "none" }
	| {
			type: "dragEdgeVertical";
			maxY: number;
			minY: number;
			fixedX: number;
			draggedX: number;
			xRange: [number, number];
	  }
	| {
			type: "dragEdgeHorizontal";
			maxX: number;
			minX: number;
			fixedY: number;
			draggedY: number;
	  }
	| {
			type: "dragVertex";
			fixedCoord: Coordinate;
			draggedCoord: Coordinate;
			cursor: string;
			xRange: [number, number];
	  }
	| {
			type: "dragBulk";
			offsetX: number;
			offsetY: number;
			width: number;
			height: number;
			draggedCoord: Coordinate;
			// xRange: [number, number];
	  }
	| {
			type: "dragMap";
			startCoord: Coordinate;
			lastPixel?: Coordinate;
	  };

const constrain = (value: number, [min, max]: [number, number]) => {
	if (value < min) {
		return min;
	}
	if (value > max) {
		return max;
	}
	return value;
};

const constrainX: ([x, y]: Coordinate, range: [number, number]) => Coordinate = ([x, y], range) => [
	constrain(x, range),
	y
];

const sameWorld: (xCoord: number) => [number, number] = xCoord => {
	const xWorlds = xCoord / worldWidth;

	// A little complexity since JavaScript Math.round does NOT round away from zero
	const numWholeWords = Math.round(Math.abs(xWorlds));
	const worldOffset = xWorlds < 0 ? -numWholeWords : numWholeWords;

	return [leftMost + worldOffset * worldWidth, rightMost + worldOffset * worldWidth];
};

const dragStateToCursor: (dragState: DragState) => string = (dragState: DragState) => {
	switch (dragState.type) {
		case "none":
			return "default";
		case "dragBulk":
			return "move";
		case "dragEdgeHorizontal":
			return "ns-resize";
		case "dragEdgeVertical":
			return "ew-resize";
		case "dragMap":
			return "grab";
		case "dragVertex":
			return dragState.cursor;
	}
};

const dragStateToCoordinates: (dragState: DragState) => [Coordinate, Coordinate] | null = (
	dragState: DragState
) => {
	switch (dragState.type) {
		case "dragVertex":
			return [dragState.fixedCoord, constrainX(dragState.draggedCoord, dragState.xRange)];
		case "dragEdgeHorizontal":
			return [
				[dragState.minX, dragState.fixedY],
				[dragState.maxX, dragState.draggedY]
			];
		case "dragEdgeVertical":
			return [
				[dragState.fixedX, dragState.minY],
				[constrain(dragState.draggedX, dragState.xRange), dragState.maxY]
			];
		case "dragBulk":
			const leftTop: Coordinate = [
				dragState.draggedCoord[0] - dragState.offsetX,
				dragState.draggedCoord[1] - dragState.offsetY
			];
			const rightBottom: Coordinate = [leftTop[0] + dragState.width, leftTop[1] + dragState.height];

			const worldXPositions = [
				Math.abs(leftTop[0]) % worldWidth,
				Math.abs(rightBottom[0]) % worldWidth
			];
			if (
				Math.min(...worldXPositions) < halfWorldWidth &&
				Math.max(...worldXPositions) > halfWorldWidth
			) {
				// Area straddles the dateline, snap to the nearest side of it
				if (
					Math.abs(worldXPositions[0] - halfWorldWidth) <
					Math.abs(worldXPositions[1] - halfWorldWidth)
				) {
					leftTop[0] += Math.abs(worldXPositions[0] - halfWorldWidth);
					rightBottom[0] += Math.abs(worldXPositions[0] - halfWorldWidth);
				} else {
					leftTop[0] -= Math.abs(worldXPositions[1] - halfWorldWidth);
					rightBottom[0] -= Math.abs(worldXPositions[1] - halfWorldWidth);
				}
			}

			return [leftTop, rightBottom];

		case "dragMap":
		case "none":
			return null;
	}
};

const coordsToArea = (coords: [Coordinate, Coordinate]) => {
	const [coord1, coord2] = coords;

	// Ordering coordinates before projecting allows for areas that span
	// the international date-line and for which west > east
	// NB This is currently forbidden since openlayers doesn't properly
	// support such features and ECMWF validation rejects them anyway
	const minX = Math.min(coord1[0], coord2[0]);
	const maxX = Math.max(coord1[0], coord2[0]);
	const minY = Math.min(coord1[1], coord2[1]);
	const maxY = Math.max(coord1[1], coord2[1]);

	// Round to 2dp and handle the -180,180 ambiguity for longitude
	const [west, north] = Proj.toLonLat([minX, maxY]).map(c => Math.round(c * 100) / 100);
	const [east, south] = Proj.toLonLat([maxX, minY]).map(c => Math.round(c * 100) / 100);

	return {
		north,
		east: east === -180 ? 180 : east,
		south,
		west: west === 180 ? -180 : west
	};
};

const areaToCoords = (area: Area): [Coordinate, Coordinate] => {
	return [
		Proj.fromLonLat([area.west, area.north]) as Coordinate,
		Proj.fromLonLat([area.east, area.south]) as Coordinate
	];
};

export const useOlFeatureEditing = ({
	mapRef,
	editableLayer,
	addMode,
	activeFeature,
	onUpdateFeature,
	grid
}: {
	mapRef: React.MutableRefObject<ol.Map | null>;
	editableLayer: React.MutableRefObject<VectorLayer | null>;
	addMode: boolean;
	activeFeature: ol.Feature | undefined;
	onUpdateFeature: (area: Area, feature?: ol.Feature) => void;
	grid?: number;
}): { cursor: string; coords?: [Coordinate, Coordinate]; area?: Area } => {
	const [dragState, setDragState] = React.useState<DragState>({ type: "none" });

	const dragStartHandler = React.useCallback(
		(x, y) => {
			if (dragState.type !== "none") {
				return;
			}

			if (addMode) {
				// All drags in add mode draw a new areas
				setDragState({
					type: "dragVertex",
					fixedCoord: [x, y],
					draggedCoord: [x, y],
					cursor: "crosshair",
					xRange: sameWorld(x)
				});
				return;
			}

			const pixel = mapRef.current!.getPixelFromCoordinate([x, y]);
			const hitFeatures = mapRef.current!.getFeaturesAtPixel(pixel, {
				hitTolerance: 20,
				layerFilter: layer => layer === editableLayer.current
			});

			// Currently only the active areas responds to drags. We could abandon the concept
			// of an active area and have the drag affect whichever area it hits.
			const feature = (hitFeatures || []).find(hit => hit === activeFeature);

			if (feature === undefined) {
				setDragState({ type: "dragMap", startCoord: [x, y] });
			} else {
				const extent = feature.getGeometry()!.getExtent();

				const [leftCoordRaw, topCoord] = Extent.getTopLeft(extent);
				const [rightCoordRaw, bottomCoord] = Extent.getBottomRight(extent);

				// For wide zoom levels, the world map and all the features may be drawn multiple times
				// so we translate the extent to match the copy that was actually hit by the pointer event
				let [leftCoord, rightCoord] = [leftCoordRaw, rightCoordRaw];

				if (x < leftCoordRaw) {
					const numWorlds = Math.floor((rightCoordRaw - x) / worldWidth);
					[leftCoord, rightCoord] = [
						leftCoordRaw - numWorlds * worldWidth,
						rightCoordRaw - numWorlds * worldWidth
					];
				} else if (x > rightCoordRaw) {
					const numWorlds = Math.floor((x - leftCoordRaw) / worldWidth);
					[leftCoord, rightCoord] = [
						leftCoordRaw + numWorlds * worldWidth,
						rightCoordRaw + numWorlds * worldWidth
					];
				}

				const centreXCoord = (leftCoord + rightCoord) / 2;

				const [left, top] = mapRef.current!.getPixelFromCoordinate([leftCoord, topCoord]);
				const [right, bottom] = mapRef.current!.getPixelFromCoordinate([rightCoord, bottomCoord]);

				const xMargin = Math.max(5, (right - left) / 8);
				const yMargin = Math.max(5, (bottom - top) / 8);

				const rightDistance = right - pixel[0];
				const leftDistance = pixel[0] - left;
				const topDistance = pixel[1] - top;
				const bottomDistance = bottom - pixel[1];

				const verticalEdges =
					leftDistance < rightDistance
						? {
								fixedX: rightCoord,
								distance: leftDistance
						  }
						: {
								fixedX: leftCoord,
								distance: rightDistance
						  };

				const horizontalEdges =
					topDistance < bottomDistance
						? {
								fixedY: bottomCoord,
								distance: topDistance
						  }
						: {
								fixedY: topCoord,
								distance: bottomDistance
						  };

				// Allow a double margin for drag vertex as it can accomplish all the same changes
				// as drag edge and therefore it is strictly more useful
				if (verticalEdges.distance < xMargin * 2 && horizontalEdges.distance < yMargin * 2) {
					setDragState({
						type: "dragVertex",
						fixedCoord: [verticalEdges.fixedX, horizontalEdges.fixedY],
						draggedCoord: [x, y],
						cursor:
							(x - verticalEdges.fixedX) * (y - horizontalEdges.fixedY) < 0
								? "nwse-resize"
								: "nesw-resize",
						xRange: sameWorld(centreXCoord)
					});
				} else if (verticalEdges.distance < xMargin) {
					setDragState({
						type: "dragEdgeVertical",
						minY: topCoord,
						maxY: bottomCoord,
						fixedX: verticalEdges.fixedX,
						draggedX: x,
						xRange: sameWorld(centreXCoord)
					});
				} else if (horizontalEdges.distance < yMargin) {
					setDragState({
						type: "dragEdgeHorizontal",
						minX: leftCoord,
						maxX: rightCoord,
						fixedY: horizontalEdges.fixedY,
						draggedY: y
					});
				} else {
					setDragState({
						type: "dragBulk",
						draggedCoord: [x, y],
						offsetX: x - leftCoord,
						offsetY: y - topCoord,
						height: bottomCoord - topCoord,
						width: rightCoord - leftCoord
					});
				}
			}
		},
		[addMode, setDragState, dragState.type, activeFeature, mapRef, editableLayer]
	);

	const dragHandler = React.useCallback(
		(x, y, pixelX, pixelY) => {
			const dragUpdate = (newState: DragState) => {
				setDragState((state: DragState) => {
					if (state.type === "none") {
						return state;
					} else {
						return newState;
					}
				});
			};
			switch (dragState.type) {
				case "dragVertex":
					dragUpdate({ ...dragState, draggedCoord: [x, y] });
					break;
				case "dragEdgeHorizontal":
					dragUpdate({ ...dragState, draggedY: y });
					break;
				case "dragEdgeVertical":
					dragUpdate({ ...dragState, draggedX: x });
					break;
				case "dragBulk":
					dragUpdate({ ...dragState, draggedCoord: [x, y] });
					break;
				case "dragMap":
					if (
						dragState.lastPixel === undefined ||
						Math.abs(dragState.lastPixel[0] - pixelX) > 1 ||
						Math.abs(dragState.lastPixel[1] - pixelY) > 1
					) {
						// We re-centre the map based on drag event pixel coordinates: using map coordinates
						// is unstable because calling setCentre() changes the pixel-to-map correspondence
						const [centreX, centreY] = mapRef.current!.getView().getCenter()!;
						const [coordX, coordY] = mapRef.current!.getCoordinateFromPixel([pixelX, pixelY]);
						const [newX, newY] = [
							centreX - coordX + dragState.startCoord[0],
							centreY - coordY + dragState.startCoord[1]
						];

						mapRef.current!.getView().setCenter([newX, newY]);
					}
					break;
			}
		},
		[dragState, setDragState, mapRef]
	);

	const dragEndHandler = React.useCallback(() => {
		setDragState({ type: "none" });

		if (dragState.type === "dragMap") {
			// Where multiple copies of the world are shown at distant zoom levels,
			// re-normalize map to ensure centreX is in the primary world
			const [centreXRaw, centreY] = mapRef.current!.getView().getCenter()!;
			let centreX = centreXRaw;

			while (centreX < leftMost) {
				centreX += worldWidth;
			}

			while (centreX > rightMost) {
				centreX -= worldWidth;
			}

			mapRef.current!.getView().setCenter([centreX, centreY]);
			return;
		}

		const coords = dragStateToCoordinates(dragState);
		if (coords !== null) {
			onUpdateFeature(coordsToArea(coords), addMode ? undefined : activeFeature);
		}
	}, [dragState, setDragState, activeFeature, addMode, mapRef, onUpdateFeature]);

	// Wire up drag and click events
	useOpenLayersDrag({
		target: mapRef,
		onDragStart: dragStartHandler,
		onDrag: dragHandler,
		onDragEnd: dragEndHandler
	});

	return React.useMemo(() => {
		const cursor = dragStateToCursor(dragState);
		const rawCoords = dragStateToCoordinates(dragState);

		if (rawCoords === null) {
			return { cursor };
		} else {
			const rawArea = coordsToArea(rawCoords);
			const snappedArea = grid ? snapAreaToGrid(rawArea, grid) : rawArea;
			const snappedCoords = areaToCoords(snappedArea);
			return {
				coords: snappedCoords,
				area: snappedArea,
				cursor
			};
		}
	}, [dragState, grid]);
};
